import React, { useEffect, useState } from "react";
import { Helper } from "../../tools/Helper";
import { api_Routes } from "../../tools/api_Routes";
import {
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  TablePagination,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { setOpen } from "../../Redux/ModalReducer";
import { FaTrashAlt, FaCheckCircle } from "react-icons/fa";
import DeleteModal from "./DeleteModal";
import VerifyModal from "./VerifyModal";
import jsPDF from "jspdf"; // Import jsPDF
import "jspdf-autotable"; // Import the plugin
import { useLocation } from "react-router-dom";

const ReviewsTable = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshData, setRefreshData] = useState(false);
  const [show, setShow] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [verifyModalShow, setVerifyModalShow] = useState(false);
  const [selectedVerifyId, setSelectedVerifyId] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [orderBy, setOrderBy] = useState("id");
  const [orderDirection, setOrderDirection] = useState("desc");
  const location = useLocation(); // Get current location
  const isServicesRoute = location.pathname.endsWith("/Services");

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage); // Ensure this updates the state correctly
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleVerifyShow = (id) => {
    setSelectedVerifyId(id);
    setVerifyModalShow(true);
    dispatch(setOpen(true));
  };

  const handleVerifyClose = () => {
    setVerifyModalShow(false);
    dispatch(setOpen(false));
  };

  const handleShow = (id) => {
    setSelectedId(id); // Set the ID of the item to be deleted
    setShow(true);
    dispatch(setOpen(true));
  };

  const handleClose = () => {
    setShow(false);
    dispatch(setOpen(false));
  };

  const getData = async () => {
    try {
      setIsLoading(true);
      const apiUrl = isServicesRoute
        ? api_Routes.Review.Services
        : api_Routes.Review.Panels;
      const { response, message } = await Helper.Get({
        url: `${apiUrl}?page=${currentPage + 1}&per_page=${rowsPerPage}`,
        hasToken: true,
      });
      if (response) {
        setData(response.data); // The paginated reviews data
        const { pagination } = response;
        if (pagination) {
          setCurrentPage(pagination.current_page - 1); // Adjust for 0-based index
          setRowsPerPage(pagination.per_page);
          setTotal(pagination.total);
        }
      } else {
        console.error("Error fetching data:", message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && orderDirection === "asc";
    setOrderBy(column);
    setOrderDirection(isAsc ? "desc" : "asc");
  };

  const sortedData = data.sort((a, b) => {
    if (orderDirection === "asc") {
      return a[orderBy] > b[orderBy] ? 1 : -1;
    }
    return a[orderBy] < b[orderBy] ? 1 : -1;
  });

  const tableColumns = [
    { label: "ID", key: "id" },
    { label: "USERNAME", key: "username" },
    isServicesRoute && { label: "Service ID", key: "service_id" }, // Conditional column
    { label: "Panel URL", key: "panel_url" },
    { label: "Rate", key: "rate" },
    { label: "Subject", key: "subject" },
    { label: "Description", key: "description" },
    { label: "Verified", key: "verified" },
    { label: "Created At", key: "created_at" },
    { label: "Actions", key: null },
  ].filter(Boolean);
  const handleExportPDF = () => {
    const doc = new jsPDF();

    // Title
    doc.text("Reviews Table", 14, 10);
    // Table columns
    const columns = [
      "ID",
      "USER NAME",
      isServicesRoute && "Service ID",
      "Panel URL",
      "Rate",
      "Subject",
      "Description",
      "Verified",
      "Created At",
    ].filter(Boolean);

    // Table rows
    const rows = sortedData
      .map((item) => [
        item.id,
        item.username,
        isServicesRoute && item.service_id,
        item.panel_url,
        item.rate,
        item.subject,
        item.description,
        item.verified ? "Yes" : "No",
        new Date(item.created_at).toLocaleDateString(),
      ])
      .filter(Boolean);

    // Add table to PDF
    doc.autoTable({
      head: [columns],
      body: rows,
      startY: 20,
    });

    // Save the PDF
    doc.save("reviews.pdf");
  };

  useEffect(() => {
    getData();
  }, [refreshData, currentPage, rowsPerPage]);
  return isLoading ? (
    <LinearProgress />
  ) : (
    <>
      {/*<Button*/}
      {/*    variant="contained"*/}
      {/*    color="primary"*/}
      {/*    onClick={handleExportPDF}*/}
      {/*    style={{ marginBottom: "16px" }}*/}
      {/*>*/}
      {/*    Export to PDF*/}
      {/*</Button>*/}
      <TableContainer component={Paper}>
        {sortedData?.length > 0 ? (
          <Table aria-label="sortable table">
            <TableHead>
              <TableRow>
                {tableColumns.map((column, idx) => (
                  <TableCell
                    key={idx}
                    align="center"
                    sx={{
                      backgroundColor: "primary.main",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    {column.key ? (
                      <TableSortLabel
                        active={orderBy === column.key}
                        direction={
                          orderBy === column.key ? orderDirection : "asc"
                        }
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell align="center">{item.id}</TableCell>
                  <TableCell align="center">{item.username}</TableCell>
                  {isServicesRoute && ( // Conditional Service ID cell
                    <TableCell align="center">{item.service_id}</TableCell>
                  )}
                  <TableCell align="center">
                    <a
                      href={item.panel_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.panel_url}
                    </a>
                  </TableCell>
                  <TableCell align="center">{item.rate}</TableCell>
                  <TableCell align="center">{item.subject}</TableCell>
                  <TableCell align="center">{item.description}</TableCell>
                  <TableCell align="center">
                    {item.verified ? (
                      <FaCheckCircle
                        className="text-green-500"
                        size={20}
                        title="Verified"
                      />
                    ) : (
                      <FaCheckCircle
                        className="text-gray-400 cursor-pointer hover:text-green-500"
                        size={20}
                        title="Click to Verify"
                        onClick={() => handleVerifyShow(item.id)}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {new Date(item.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <FaTrashAlt
                      onClick={() => handleShow(item.id)}
                      className="text-[#d9534f] cursor-pointer ml-2"
                      size={20}
                      title="Delete Review"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]} // Provide options for rows per page
              component="div"
              count={total} // Use pagination.total from the response
              rowsPerPage={rowsPerPage} // Set from state
              page={currentPage} // Set from state
              onPageChange={(_, newPage) => {
                setCurrentPage(newPage); // Update current page
              }}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10)); // Update rows per page
                setCurrentPage(0); // Reset to the first page
              }}
              sx={{ overflow: "visible" }}
            />
          </Table>
        ) : (
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  There are no reviews yet
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        )}
      </TableContainer>
      <DeleteModal
        show={show}
        handleClose={handleClose}
        selectedId={selectedId}
        setShouldRefresh={setRefreshData}
        isService={isServicesRoute}
      />
      <VerifyModal
        show={verifyModalShow}
        handleClose={handleVerifyClose}
        selectedId={selectedVerifyId}
        triggerRefresh={setRefreshData}
        isService={isServicesRoute}
      />
    </>
  );
};

export default ReviewsTable;
